type ZoneKey = 'norte' | 'rm' | 'centro' | 'sur';
type ZoneFilter = ZoneKey | 'all';

interface MapPointPhone {
  id: number;
  dist: number;
  telefono: string;
  nombre: string;
  whatsapp: number;
  orden: number;
}

interface MapPointApi {
  id: number;
  titulo: string;
  rut: string;
  direccion: string;
  comuna: number;
  tipo: number;
  lat: string;
  lng: string;
  url: string;
  email: string;
  adicional: string;
  portada: string;
  googlelink: string;
  activo: number;
  marca: number;
  comuna_name: string;
  provincia_name: string;
  region_name: string;
  telefonos: MapPointPhone[];
}

interface MapPoint extends MapPointApi {
  zone: ZoneKey;
}

const DEFAULT_BRAND_COLOR = '#1e88e5';
const FONT_AWESOME_LOCAL_PATH = '/assets/fontawesome/css/all.min.css';
const BRAND_COLORS: Record<string, string> = {
  ktm: '#ff6600'
};
const TYPE_CODES: Record<string, number> = {
  sucursal: 0,
  distribuidor: 1
};
const ZONE_ORDER: ZoneKey[] = ['norte', 'rm', 'centro', 'sur'];
const ZONE_LABELS: Record<ZoneKey, string> = {
  norte: 'Zona Norte',
  rm: 'RM',
  centro: 'Zona Centro',
  sur: 'Zona Sur'
};
const TYPE_LABELS: Record<number, string> = {
  0: 'Sucursales',
  1: 'Distribuidores'
};
const REGION_ZONE_MAP: Record<string, ZoneKey> = {
  'region de arica y parinacota': 'norte',
  'arica y parinacota': 'norte',
  'region de tarapaca': 'norte',
  tarapaca: 'norte',
  'region de antofagasta': 'norte',
  antofagasta: 'norte',
  'region de atacama': 'norte',
  atacama: 'norte',
  'region de coquimbo': 'norte',
  coquimbo: 'norte',
  'region de valparaiso': 'centro',
  valparaiso: 'centro',
  'region del libertador general bernardo o higgins': 'centro',
  "region del libertador general bernardo o'higgins": 'centro',
  "libertador bernardo o'higgins": 'centro',
  ohiggins: 'centro',
  'region del maule': 'centro',
  maule: 'centro',
  'region de nuble': 'centro',
  nuble: 'centro',
  'region del biobio': 'centro',
  biobio: 'centro',
  'region metropolitana de santiago': 'rm',
  'region metropolitana': 'rm',
  'rm': 'rm',
  santiago: 'rm',
  'region de la araucania': 'sur',
  araucania: 'sur',
  'region de los rios': 'sur',
  'los rios': 'sur',
  'region de los lagos': 'sur',
  'los lagos': 'sur',
  'region de aysen del general carlos ibanez del campo': 'sur',
  'region de aysen': 'sur',
  aysen: 'sur',
  'region de magallanes y la antartica chilena': 'sur',
  magallanes: 'sur'
};

export class RsMap extends HTMLElement {
  static tag = 'rs-map';
  private root: ShadowRoot;
  private mapEl!: HTMLDivElement;
  private map: any;
  private markers: any[] = [];
  private marca = '';
  private marcaKey = 'default';
  private tipo = '';
  private tipoCode: number | null = null;
  private pointsController?: AbortController;
  private points: MapPoint[] = [];
  private pointsByZone: Record<ZoneKey, MapPoint[]> = {
    norte: [],
    rm: [],
    centro: [],
    sur: []
  };
  private initialViewApplied = false;
  private zonesContainer!: HTMLElement;
  private componentMessageEl!: HTMLElement;
  private mapLayoutEl!: HTMLElement;
  private zoneButtons: HTMLButtonElement[] = [];
  private nearestButton!: HTMLButtonElement;
  private panelTitleEl!: HTMLElement;
  private activeZone: ZoneFilter = 'all';
  private mapHeight = '';
  private googleMapsApiKey = '';
  private pointsApiUrl = '';
  private markerIconBaseUrl = '';
  private defaultMarkerIconUrl = '';
  private static fontAwesomeAttached = false;
  private panAnimationFrame: number | null = null;
  private highlightTimeout: number | null = null;

  static get observedAttributes() {
    return [
      'marca',
      'tipo',
      'height',
      'google-maps-key',
      'points-api-url',
      'marker-icon-base',
      'marker-icon-default'
    ];
  }

  constructor() {
    super();
    RsMap.ensureFontAwesome();
    this.root = this.attachShadow({ mode: 'open' });
    this.root.innerHTML = `
      <link rel="stylesheet" href="${FONT_AWESOME_LOCAL_PATH}" />
      <style>
        :host {
          display: block;
          width: 100%;
          height: var(--map-height, 520px);
          --rs-map-accent: ${DEFAULT_BRAND_COLOR};
        }
        .map-layout {
          display: grid;
          grid-template-columns: minmax(0, 2.5fr) minmax(220px, 1fr);
          gap: 16px;
          width: 100%;
          height: 100%;
        }
        .map-panel,
        #map {
          width: 100%;
          height: 100%;
          animation: mapSlide 90s infinite;
            animation-timing-function: ease-in-out; 
        }
        .map-panel {
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid var(--rs-map-accent);
        }
        #map {
          background: #f5f5f5;
        }
        .component-shell {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .component-message {
          margin: 0;
          font-size: 1rem;
          color: #555;
          text-align: center;
          padding: 24px 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
        }
        .component-message[hidden] {
          display: none;
        }
        .map-layout[hidden] {
          display: none;
        }
        .points-panel {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          border-top: 4px solid var(--rs-map-accent);
          padding: 16px;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #fff;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .panel-message {
          margin: 0;
          font-size: 0.95rem;
          color: #555;
          text-align: center;
          padding: 32px 12px;
        }
        .panel-content[hidden] {
          display: none;
        }
        .points-panel h2 {
          margin: 0 0 8px;
          font-size: 1rem;
          color: var(--rs-map-accent);
        }
        .zone-filters {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
          overflow-x: auto;
          padding-bottom: 8px;
          flex-wrap: nowrap;
        }
        .zone-button {
          border: 1px solid var(--rs-map-accent);
          border-radius: 999px;
          padding: 6px 12px;
          font-size: 0.85rem;
          background: transparent;
          color: var(--rs-map-accent);
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease;
          text-align: center;
          white-space: nowrap;
        }
        .zone-button:hover,
        .zone-button:focus-visible {
          background: var(--rs-map-accent);
          color: #fff;
        }
        .zone-button.is-active {
          background: var(--rs-map-accent);
          color: #fff;
        }
        .zone-button--secondary {
          flex-shrink: 0;
          border-color: #444;
          color: #444;
        }
        .zone-button--secondary i {
          margin-right: 6px;
        }
        .panel-header {
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .panel-header h2[data-panel-title] {
          margin: 0;
          font-size: 1.25rem;
        }
        .zones-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          flex: 1;
          overflow-y: auto;
        }
        .zone-section {
          border-top: 2px solid #eee;
          padding-top: 12px;
        }
        .zone-section[hidden] {
          display: none;
        }
        .zone-section__header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          margin: 0 0 8px;
          font-weight: 600;
          color: #333;
        }
        .zone-section__empty {
          margin: 0;
          font-size: 0.85rem;
          color: #777;
        }
        .zone-section__list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .point-card {
          border: 1px solid #e6e6e6;
          border-radius: 8px;
          padding: 10px;
          background: #fff;
          display: flex;
          flex-direction: column;
          gap: 6px;
          overflow: hidden;
          transition: border 0.2s ease, box-shadow 0.2s ease;
        }
        .point-card--highlight {
          border: 2px solid var(--rs-map-accent);
          box-shadow: 0 0 0 2px rgba(30, 136, 229, 0.2);
          animation: highlightFade 3s forwards;
        }
        @keyframes highlightFade {
          0% {
            border-color: var(--rs-map-accent);
            box-shadow: 0 0 0 2px rgba(30, 136, 229, 0.2);
          }
          80% {
            border-color: #111;
            box-shadow: 0 0 0 2px rgba(17, 17, 17, 0.2);
          }
          100% {
            border-color: #e6e6e6;
            box-shadow: none;
          }
        }
        .point-card__title {
          margin: -10px -10px 8px;
          padding: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          text-align: center;
          color: #fff;
          background: var(--rs-map-accent);
        }
        .point-card__focus {
          border: 1px solid var(--rs-map-accent);
          border-radius: 999px;
          padding: 4px 12px;
          font-size: 0.8rem;
          background: transparent;
          color: var(--rs-map-accent);
          cursor: pointer;
          align-self: flex-start;
          margin-top: 8px;
        }
        .point-card__focus:hover,
        .point-card__focus:focus-visible {
          background: var(--rs-map-accent);
          color: #fff;
        }
        @media (max-width: 768px) {
          .map-layout {
            grid-template-columns: 1fr;
          }
          .map-panel {
            display: none;
          }
          .panel-header {
            flex-direction: column;
            align-items: stretch;
          }
          .zone-filters {
            flex-wrap: wrap;
          }
          .point-card__focus {
            display: none;
          }
        }
        .point-card__address,
        .point-card__meta,
        .point-card__contact {
          margin: 0;
          font-size: 0.85rem;
          color: #555;
        }
        .point-card__contact {
          margin: 0 0 6px;
          font-size: 0.85rem;
          color: #555;
        }
        .point-card__contact-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          color: #333;
          margin-bottom: 4px;
        }
        .point-card__phone-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .point-card__phone-item {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }
        .point-card__phone-icon {
          width: 16px;
          display: inline-flex;
          justify-content: center;
          color: var(--rs-map-accent);
          font-size: 0.85rem;
        }
        .point-card__phone-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .point-card__phone-name {
          font-weight: 600;
          color: #333;
        }
        .point-card__phone-number {
          font-family: 'Segoe UI', system-ui, sans-serif;
          font-size: 0.85rem;
        }
        .point-card__phone-number--link {
          color: var(--rs-map-accent);
          text-decoration: none;
        }
        .point-card__phone-number--link:hover,
        .point-card__phone-number--link:focus-visible {
          text-decoration: underline;
        }
        .point-card__address-block {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .point-card__details {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 6px;
        }
        .point-card__details[hidden] {
          display: none;
        }
        .point-card__toggle {
          border: 1px solid var(--rs-map-accent);
          border-radius: 16px;
          padding: 4px 10px;
          background: transparent;
          color: var(--rs-map-accent);
          font-size: 0.8rem;
          cursor: pointer;
          align-self: flex-start;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .point-card__toggle:hover,
        .point-card__toggle:focus-visible {
          background: var(--rs-map-accent);
          color: #fff;
        }
        .point-card__actions {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
          margin-top: 8px;
        }
        .point-card__action {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
        }
        .point-card__actions a.point-card__action {
          color: var(--rs-map-accent);
          text-decoration: none;
          border-bottom: 1px solid transparent;
        }
        .point-card__actions a.point-card__action:hover,
        .point-card__actions a.point-card__action:focus-visible {
          border-color: currentColor;
        }
        .point-card__actions .point-card__action i {
          font-size: 0.85rem;
        }
        .point-card__actions .point-card__action--text {
          color: #444;
        }
        .point-card__extra {
          margin: 0;
          font-size: 0.8rem;
          color: #444;
        }
        .point-card__extra a {
          color: var(--rs-map-accent);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .point-card__extra a:hover,
        .point-card__extra a:focus-visible {
          color: #444;
          text-decoration: underline;
        }
        .point-card__extra-icon {
          font-size: 0.75rem;
        }
        .empty-state {
          margin: 0;
          font-size: 0.9rem;
          color: #777;
          text-align: center;
        }
        .points-panel p {
          margin: 0;
          color: #666;
          font-size: 0.9rem;
        }
      </style>
      <div class="component-shell">
        <p class="component-message" data-component-message>Cargando puntos...</p>
        <div class="map-layout" data-map-layout hidden>
          <div class="map-panel">
            <div id="map"></div>
          </div>
          <aside class="points-panel" aria-live="polite">
            <div class="zone-filters" role="group" aria-label="Filtrar por zona">
              <button class="zone-button" type="button" data-zone="norte" data-label="Zona Norte" aria-pressed="false">Zona Norte</button>
              <button class="zone-button" type="button" data-zone="rm" data-label="RM" aria-pressed="false">RM</button>
              <button class="zone-button" type="button" data-zone="centro" data-label="Zona Centro" aria-pressed="false">Zona Centro</button>
              <button class="zone-button" type="button" data-zone="sur" data-label="Zona Sur" aria-pressed="false">Zona Sur</button>
            </div>
            <div class="panel-header">
              <h2 data-panel-title>Puntos del mapa</h2>
              <button class="zone-button zone-button--secondary" type="button" data-action="nearest">
                <i class="fa-solid fa-location-dot" aria-hidden="true"></i>
                Buscar tienda cercana
              </button>
            </div>
            <div class="zones-container" data-zone-container></div>
          </aside>
        </div>
      </div>
    `;
  }

  connectedCallback() {
    this.mapEl = this.root.querySelector<HTMLDivElement>('#map')!;
    this.panelTitleEl = this.root.querySelector<HTMLElement>('[data-panel-title]')!;
    this.zonesContainer = this.root.querySelector<HTMLElement>('[data-zone-container]')!;
    this.componentMessageEl = this.root.querySelector<HTMLElement>('[data-component-message]')!;
    this.mapLayoutEl = this.root.querySelector<HTMLElement>('[data-map-layout]')!;
    this.zoneButtons = Array.from(this.root.querySelectorAll<HTMLButtonElement>('[data-zone]'));
    this.nearestButton = this.root.querySelector<HTMLButtonElement>('[data-action="nearest"]')!;
    this.zoneButtons.forEach((btn) => btn.addEventListener('click', this.handleZoneButtonClick));
    if (this.nearestButton) {
      this.nearestButton.addEventListener('click', this.handleNearestClick);
    }
    this.updateZoneButtons();
    this.setMapHeight(this.getAttribute('height'));
    this.setComponentState('loading');
    this.syncAttributesToState();
    this.loadGoogleMaps();
  }

  disconnectedCallback() {
    this.pointsController?.abort();
    this.zoneButtons.forEach((btn) => btn.removeEventListener('click', this.handleZoneButtonClick));
    if (this.nearestButton) {
      this.nearestButton.removeEventListener('click', this.handleNearestClick);
    }
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null) {
    if (name === 'marca') {
      this.setMarca(newValue ?? '');
    } else if (name === 'tipo') {
      this.setTipo(newValue ?? '');
    } else if (name === 'height') {
      this.setMapHeight(newValue);
    } else if (name === 'google-maps-key') {
      this.setGoogleMapsKey(newValue);
    } else if (name === 'points-api-url') {
      this.setPointsApiUrl(newValue);
    } else if (name === 'marker-icon-base') {
      this.setMarkerIconBase(newValue);
    } else if (name === 'marker-icon-default') {
      this.setDefaultMarkerIcon(newValue);
    }
  }

  private async loadGoogleMaps() {
    // Evita cargar dos veces el script
    if ((window as any)._rsGoogleMapsLoaded) {
      this.initMap();
      return;
    }

    if (!this.googleMapsApiKey) {
      console.error('Falta la clave de Google Maps. Define el atributo "google-maps-key" en <rs-map>.');
      return;
    }

    const script = document.createElement('script');
    const params = new URLSearchParams({
      key: this.googleMapsApiKey,
      v: 'weekly'
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      (window as any)._rsGoogleMapsLoaded = true;
      this.initMap();
    };

    script.onerror = () => {
      console.error('Error al cargar la API de Google Maps');
    };

    document.head.appendChild(script);
  }

  private initMap() {
    const g = (window as any).google;
    if (!g) {
      console.error('Google Maps aun no esta disponible.');
      return;
    }

    const initialCenter = this.getInitialCenter();
    this.map = new g.maps.Map(this.mapEl, {
      center: initialCenter,
      zoom: 14,
      mapTypeId: 'roadmap'
    });
    if (this.points.length) {
      this.updateMarkers(this.points);
    }
  }

  private getInitialCenter() {
    const firstPoint = this.getFirstValidPoint();
    if (firstPoint) {
      return firstPoint;
    }
    return { lat: -33.4489, lng: -70.6693 };
  }

  private getFirstValidPoint(): { lat: number; lng: number } | null {
    for (const point of this.points) {
      const lat = Number(point.lat);
      const lng = Number(point.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
    }
    return null;
  }

  private syncAttributesToState() {
    this.setMarca(this.getAttribute('marca') ?? '');
    this.setTipo(this.getAttribute('tipo') ?? '');
    this.setGoogleMapsKey(this.getAttribute('google-maps-key'));
    this.setPointsApiUrl(this.getAttribute('points-api-url'));
    this.setMarkerIconBase(this.getAttribute('marker-icon-base'));
    this.setDefaultMarkerIcon(this.getAttribute('marker-icon-default'));
  }

  private setMapHeight(value: string | null) {
    const normalized = (value ?? '').trim();
    if (!normalized) {
      this.mapHeight = '';
      this.style.removeProperty('--map-height');
      return;
    }
    this.mapHeight = normalized;
    this.style.setProperty('--map-height', normalized);
  }

  private setGoogleMapsKey(value: string | null) {
    this.googleMapsApiKey = (value ?? '').trim();
    if (this.googleMapsApiKey && this.isConnected && !(window as any)._rsGoogleMapsLoaded) {
      this.loadGoogleMaps();
    }
  }

  private setPointsApiUrl(value: string | null) {
    this.pointsApiUrl = (value ?? '').trim();
    if (this.pointsApiUrl && this.isConnected) {
      this.refreshPoints();
    }
  }

  private setMarkerIconBase(value: string | null) {
    const normalized = (value ?? '').trim();
    if (!normalized) {
      this.markerIconBaseUrl = '';
      return;
    }
    this.markerIconBaseUrl = normalized.endsWith('/') ? normalized : `${normalized}/`;
  }

  private setDefaultMarkerIcon(value: string | null) {
    this.defaultMarkerIconUrl = (value ?? '').trim();
  }

  private setMarca(value: string) {
    this.marca = value.trim();
    this.marcaKey = this.normalizeBrandKey(this.marca);
    this.applyBrandColor();
    this.refreshPoints();
  }

  private setTipo(value: string) {
    this.tipo = value.trim();
    this.tipoCode = this.normalizeTipo(this.tipo);
    this.updatePanelTitle();
    this.refreshPoints();
  }

  get marcaFilter() {
    return this.marca;
  }

  get tipoFilter() {
    return this.tipo;
  }

  private updatePanelTitle() {
    if (!this.panelTitleEl) {
      return;
    }
    const label = this.getTipoLabel();
    this.panelTitleEl.textContent = label ?? 'Puntos del mapa';
  }

  private getTipoLabel() {
    if (this.tipoCode !== null && this.tipoCode in TYPE_LABELS) {
      return TYPE_LABELS[this.tipoCode];
    }
    const normalized = this.tipo.trim().toLowerCase();
    const normalizedCode = normalized && normalized in TYPE_CODES ? TYPE_CODES[normalized] : null;
    if (normalizedCode !== null && normalizedCode in TYPE_LABELS) {
      return TYPE_LABELS[normalizedCode];
    }
    return null;
  }

  private applyBrandColor() {
    const color = BRAND_COLORS[this.marcaKey] ?? DEFAULT_BRAND_COLOR;
    this.style.setProperty('--rs-map-accent', color);
  }

  private normalizeTipo(value: string): number | null {
    if (!value) {
      return null;
    }
    const normalized = value.trim().toLowerCase();
    if (normalized in TYPE_CODES) {
      return TYPE_CODES[normalized];
    }
    const numeric = Number(normalized);
    if (Number.isFinite(numeric) && (numeric === 0 || numeric === 1)) {
      return numeric;
    }
    return null;
  }

  private async refreshPoints() {
    if (!this.isConnected || !this.marca || this.tipoCode === null) {
      return;
    }

    if (!this.pointsApiUrl) {
      console.error('Falta la URL de la API de puntos. Define el atributo "points-api-url" en <rs-map>.');
      this.setComponentState('error');
      return;
    }

    this.pointsController?.abort();
    const controller = new AbortController();
    this.pointsController = controller;

    const params = new URLSearchParams({
      marca: this.marca,
      tipo: String(this.tipoCode)
    });

    const hasQuery = this.pointsApiUrl.includes('?');
    const needsAmp = hasQuery && !/[?&]$/.test(this.pointsApiUrl);
    const separator = hasQuery ? (needsAmp ? '&' : '') : '?';
    const requestUrl = `${this.pointsApiUrl}${separator}${params.toString()}`;

    this.setComponentState('loading');

    try {
      const response = await fetch(requestUrl, {
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Respuesta ${response.status}`);
      }

      const payload = await response.json();
      if (!Array.isArray(payload)) {
        throw new Error('La respuesta de puntos no es valida.');
      }

      this.points = this.transformPoints(payload as MapPointApi[]);
      this.renderPoints(this.points);
      this.initialViewApplied = false;
      this.updateMarkers(this.points);
      this.setComponentState('ready');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error('No se pudieron cargar los puntos del mapa', error);
      this.setComponentState('error');
    }
  }

  private transformPoints(payload: MapPointApi[]): MapPoint[] {
    return payload.map((point) => ({
      ...point,
      zone: this.getZoneForPoint(point)
    }));
  }

  private renderPoints(points: MapPoint[]) {
    if (!this.zonesContainer) {
      return;
    }

    const grouped = this.groupPoints(points);
    this.pointsByZone = grouped;
    this.updateZoneButtonCounts();

    const fragment = document.createDocumentFragment();

    ZONE_ORDER.forEach((zoneKey) => {
      const section = document.createElement('section');
      section.className = 'zone-section';
      section.dataset.zoneSection = zoneKey;

      const header = document.createElement('div');
      header.className = 'zone-section__header';
      const title = document.createElement('span');
      title.textContent = ZONE_LABELS[zoneKey];
      header.appendChild(title);
      section.appendChild(header);

      if (grouped[zoneKey].length === 0) {
        const empty = document.createElement('p');
        empty.className = 'zone-section__empty';
        empty.textContent = 'Sin puntos en esta zona.';
        section.appendChild(empty);
      } else {
        const list = document.createElement('div');
        list.className = 'zone-section__list';
        grouped[zoneKey].forEach((point) => {
          list.appendChild(this.createPointCard(point));
        });
        section.appendChild(list);
      }

      fragment.appendChild(section);
    });

    this.zonesContainer.replaceChildren(fragment);
    this.updateZoneVisibility();
  }

  private groupPoints(points: MapPoint[]): Record<ZoneKey, MapPoint[]> {
    const grouped: Record<ZoneKey, MapPoint[]> = {
      norte: [],
      rm: [],
      centro: [],
      sur: []
    };
    points.forEach((point) => {
      grouped[point.zone].push(point);
    });
    return grouped;
  }

  private createPointCard(point: MapPoint) {
    const card = document.createElement('article');
    card.className = 'point-card';
    card.dataset.pointId = String(point.id);

    const title = document.createElement('h3');
    title.className = 'point-card__title';
    title.textContent = point.titulo || 'Sin nombre';
    card.appendChild(title);

    const addressBlock = document.createElement('div');
    addressBlock.className = 'point-card__address-block';

    const address = document.createElement('p');
    address.className = 'point-card__address';
    address.textContent = point.direccion || 'Sin direccion';
    addressBlock.appendChild(address);

    if (point.comuna_name) {
      const comuna = document.createElement('p');
      comuna.className = 'point-card__meta';
      comuna.textContent = point.comuna_name;
      addressBlock.appendChild(comuna);
    }

    if (point.provincia_name) {
      const provincia = document.createElement('p');
      provincia.className = 'point-card__meta';
      provincia.textContent = point.provincia_name;
      addressBlock.appendChild(provincia);
    }

    if (point.region_name) {
      const regionLine = document.createElement('p');
      regionLine.className = 'point-card__meta';
      regionLine.textContent = point.region_name;
      addressBlock.appendChild(regionLine);
    }

    card.appendChild(addressBlock);

    let focusButton: HTMLButtonElement | null = null;
    const lat = Number(point.lat);
    const lng = Number(point.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      focusButton = document.createElement('button');
      focusButton.type = 'button';
      focusButton.className = 'point-card__focus';
      const icon = this.createIconElement('fa-solid fa-location-crosshairs');
      focusButton.appendChild(icon);
      focusButton.appendChild(document.createTextNode(' Ver en el mapa'));
      focusButton.addEventListener('click', () => {
        void this.focusPointWithZoom(lat, lng);
        this.scrollToPointCard(point.id);
      });
    }

    const details = document.createElement('div');
    details.className = 'point-card__details';
    details.hidden = true;
    let hasDetails = false;

    const actions = document.createElement('div');
    actions.className = 'point-card__actions';
    let mapLinkElement: HTMLAnchorElement | null = null;

    if (point.url && point.url !== '#') {
      const siteLink = document.createElement('a');
      siteLink.href = point.url;
      siteLink.target = '_blank';
      siteLink.rel = 'noopener noreferrer';
      siteLink.className = 'point-card__action';
      const icon = this.createIconElement('fa-solid fa-globe');
      siteLink.appendChild(icon);
      siteLink.appendChild(document.createTextNode('Sitio web'));
      actions.appendChild(siteLink);
    }

    if (point.email) {
      const emailLink = document.createElement('a');
      emailLink.href = `mailto:${point.email}`;
      emailLink.target = '_blank';
      emailLink.rel = 'noopener noreferrer';
      emailLink.className = 'point-card__action point-card__action--text';
      const icon = this.createIconElement('fa-solid fa-envelope');
      emailLink.appendChild(icon);
      emailLink.appendChild(document.createTextNode(point.email));
      actions.appendChild(emailLink);
    }

    if (actions.childElementCount) {
      details.appendChild(actions);
      hasDetails = true;
    }

    if (point.telefonos?.length) {
      const contactSection = document.createElement('div');
      contactSection.className = 'point-card__contact';
      const contactTitle = document.createElement('div');
      contactTitle.className = 'point-card__contact-title';
      const icon = this.createIconElement('fa-solid fa-phone');
      contactTitle.appendChild(icon);
      contactTitle.appendChild(document.createTextNode('Telefonos'));
      contactSection.appendChild(contactTitle);

      const phoneList = document.createElement('ul');
      phoneList.className = 'point-card__phone-list';

      point.telefonos
        .slice()
        .sort((a, b) => a.orden - b.orden)
        .forEach((phone) => {
          const item = document.createElement('li');
          item.className = 'point-card__phone-item';

          const iconWrapper = document.createElement('span');
          iconWrapper.className = 'point-card__phone-icon';
          if (phone.whatsapp) {
            const whatsappIcon = this.createIconElement('fa-brands fa-whatsapp');
            iconWrapper.appendChild(whatsappIcon);
          } else {
            iconWrapper.textContent = '-';
          }
          item.appendChild(iconWrapper);

          const content = document.createElement('div');
          content.className = 'point-card__phone-content';

          const name = document.createElement('span');
          name.className = 'point-card__phone-name';
          name.textContent = phone.nombre;
          content.appendChild(name);

          const numberEl = phone.whatsapp ? document.createElement('a') : document.createElement('span');
          numberEl.className = 'point-card__phone-number';
          if (phone.whatsapp) {
            const link = this.getWhatsappLink(phone.telefono);
            if (link) {
              (numberEl as HTMLAnchorElement).href = link;
              (numberEl as HTMLAnchorElement).target = '_blank';
              (numberEl as HTMLAnchorElement).rel = 'noopener noreferrer';
              numberEl.classList.add('point-card__phone-number--link');
            }
          }
          numberEl.textContent = phone.telefono;
          content.appendChild(numberEl);

          item.appendChild(content);
          phoneList.appendChild(item);
        });

      contactSection.appendChild(phoneList);
      details.appendChild(contactSection);
      hasDetails = true;
    }

    if (point.adicional) {
      const extra = document.createElement('p');
      extra.className = 'point-card__extra';
      extra.innerHTML = point.adicional;
      extra.querySelectorAll('a').forEach((link) => {
        link.classList.add('point-card__extra-link');
        const icon = this.createIconElement('fa-solid fa-arrow-up-right-from-square');
        icon.classList.add('point-card__extra-icon');
        link.appendChild(icon);
      });
      details.appendChild(extra);
      hasDetails = true;
    }

    if (point.googlelink) {
      mapLinkElement = document.createElement('a');
      mapLinkElement.href = point.googlelink;
      mapLinkElement.target = '_blank';
      mapLinkElement.rel = 'noopener noreferrer';
      mapLinkElement.className = 'point-card__action';
      const icon = this.createIconElement('fa-solid fa-map-location-dot');
      mapLinkElement.appendChild(icon);
      mapLinkElement.appendChild(document.createTextNode('Google Maps'));
    }

    if (mapLinkElement) {
      const mapWrapper = document.createElement('div');
      mapWrapper.className = 'point-card__actions';
      mapWrapper.appendChild(mapLinkElement);
      details.appendChild(mapWrapper);
      hasDetails = true;
    }

    if (hasDetails) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'point-card__toggle';
      const toggleIcon = this.createIconElement('fa-solid fa-circle-info');
      const toggleText = document.createElement('span');
      toggleText.textContent = 'Mas informacion';
      toggle.appendChild(toggleIcon);
      toggle.appendChild(toggleText);
      toggle.addEventListener('click', () => {
        const isHidden = details.hidden;
        details.hidden = !isHidden;
        toggleText.textContent = isHidden ? 'Ocultar informacion' : 'Mas informacion';
        toggleIcon.className = isHidden ? 'fa-solid fa-circle-xmark' : 'fa-solid fa-circle-info';
      });
      card.appendChild(toggle);
      card.appendChild(details);
    }

    if (focusButton) {
      card.appendChild(focusButton);
    }

    return card;
  }

  private getZoneForPoint(point: MapPointApi): ZoneKey {
    const normalizedRegion = this.normalizeRegionName(point.region_name || '');
    if (normalizedRegion && normalizedRegion in REGION_ZONE_MAP) {
      return REGION_ZONE_MAP[normalizedRegion];
    }
    if (normalizedRegion.includes('metropolitana')) {
      return 'rm';
    }
    if (
      normalizedRegion.includes('araucania') ||
      normalizedRegion.includes('rios') ||
      normalizedRegion.includes('lagos') ||
      normalizedRegion.includes('aysen') ||
      normalizedRegion.includes('magallanes')
    ) {
      return 'sur';
    }
    if (
      normalizedRegion.includes('arica') ||
      normalizedRegion.includes('tarapaca') ||
      normalizedRegion.includes('antofagasta') ||
      normalizedRegion.includes('atacama') ||
      normalizedRegion.includes('coquimbo')
    ) {
      return 'norte';
    }
    return 'centro';
  }

  private normalizeRegionName(input: string) {
    return input
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private normalizeBrandKey(value: string) {
    if (!value) {
      return 'default';
    }
    const normalized = value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '');
    return normalized || 'default';
  }

  private getMarkerIcon() {
    const key = this.marcaKey || 'default';
    if (this.markerIconBaseUrl) {
      return `${this.markerIconBaseUrl}${key}.png`;
    }
    if (this.defaultMarkerIconUrl) {
      return this.defaultMarkerIconUrl;
    }
    return null;
  }

  private updateMarkers(points: MapPoint[]) {
    const g = (window as any).google;
    if (!g || !this.map) {
      return;
    }
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];

    const validPoints = points.filter((point) => {
      const lat = Number(point.lat);
      const lng = Number(point.lng);
      return Number.isFinite(lat) && Number.isFinite(lng);
    });

    if (!validPoints.length) {
      return;
    }

    const bounds = new g.maps.LatLngBounds();
    const iconUrl = this.getMarkerIcon();
    const icon =
      iconUrl && this.map
        ? {
            url: iconUrl,
            scaledSize: new g.maps.Size(36, 36),
            anchor: new g.maps.Point(18, 36)
          }
        : undefined;

    validPoints.forEach((point) => {
      const lat = Number(point.lat);
      const lng = Number(point.lng);
      const marker = new g.maps.Marker({
        map: this.map,
        position: { lat, lng },
        title: point.titulo,
        icon
      });
      this.markers.push(marker);
      const position = marker.getPosition();
      if (position) {
        bounds.extend(position);
      }
    });

    if (!this.initialViewApplied && this.markers.length) {
      const position = this.markers[0].getPosition();
      if (position) {
        this.map.setCenter(position);
        this.map.setZoom(14);
        this.initialViewApplied = true;
        return;
      }
    }

    if (this.markers.length === 1) {
      const position = this.markers[0].getPosition();
      if (position) {
        this.map.setCenter(position);
      }
      this.map.setZoom(14);
    } else {
      this.map.fitBounds(bounds);
    }
  }

  private handleZoneButtonClick = (event: Event) => {
    const button = event.currentTarget as HTMLButtonElement;
    const zone = button.dataset.zone as ZoneKey;
    if (!zone) {
      return;
    }
    this.setActiveZone(zone);
    this.scrollToZoneSection(zone);
  };

  private setActiveZone(_zone: ZoneFilter) {}

  private scrollToZoneSection(zone: ZoneKey) {
    if (!this.zonesContainer) {
      return;
    }
    const section = this.zonesContainer.querySelector<HTMLElement>(`[data-zone-section="${zone}"]`);
    if (!section) {
      return;
    }
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private updateZoneButtons() {
    this.zoneButtons.forEach((btn) => {
      const zone = btn.dataset.zone as ZoneKey;
      const isActive = this.activeZone === zone;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });
  }

  private updateZoneButtonCounts() {
    this.zoneButtons.forEach((btn) => {
      const zone = btn.dataset.zone as ZoneKey;
      const baseLabel = btn.dataset.label || btn.textContent || '';
      btn.textContent = baseLabel;
    });
  }

  private updateZoneVisibility() {
    if (!this.zonesContainer) {
      return;
    }
    const sections = this.zonesContainer.querySelectorAll<HTMLElement>('[data-zone-section]');
    sections.forEach((section) => {
      section.removeAttribute('hidden');
    });
  }

  private handleNearestClick = () => {
    if (!navigator.geolocation) {
      alert('La geolocalizacion no esta soportada en este navegador.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const nearest = this.findNearestPoint(latitude, longitude);
        if (nearest) {
          this.centerMapOnPoint(Number(nearest.lat), Number(nearest.lng));
          this.scrollToPointCard(nearest.id);
        } else {
          alert('No hay puntos disponibles para calcular la tienda cercana.');
        }
      },
      () => {
        alert('No se pudo obtener tu ubicacion.');
      }
    );
  };

  private findNearestPoint(lat: number, lng: number): MapPoint | null {
    if (!this.points.length) {
      return null;
    }
    let nearest: MapPoint | null = null;
    let bestDistance = Infinity;
    this.points.forEach((point) => {
      const pointLat = Number(point.lat);
      const pointLng = Number(point.lng);
      if (!Number.isFinite(pointLat) || !Number.isFinite(pointLng)) {
        return;
      }
      const distance = this.haversine(lat, lng, pointLat, pointLng);
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = point;
      }
    });
    return nearest;
  }

  private haversine(lat1: number, lon1: number, lat2: number, lon2: number) {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const R = 6371e3;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private scrollToPointCard(pointId: number) {
    if (!this.zonesContainer) {
      return;
    }
    const card = this.zonesContainer.querySelector<HTMLElement>(`[data-point-id="${pointId}"]`);
    if (!card) {
      return;
    }
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.highlightPointCard(card);
  }

  private highlightPointCard(card: HTMLElement) {
    if (this.highlightTimeout !== null) {
      clearTimeout(this.highlightTimeout);
      this.highlightTimeout = null;
    }
    this.zonesContainer?.querySelectorAll('.point-card--highlight').forEach((el) => {
      el.classList.remove('point-card--highlight');
    });
    card.classList.add('point-card--highlight');
    this.highlightTimeout = window.setTimeout(() => {
      card.classList.remove('point-card--highlight');
      this.highlightTimeout = null;
    }, 3500);
  }

  private setComponentState(state: 'loading' | 'ready' | 'error') {
    if (!this.componentMessageEl || !this.mapLayoutEl) {
      return;
    }
    if (state === 'ready') {
      this.componentMessageEl.setAttribute('hidden', '');
      this.mapLayoutEl.removeAttribute('hidden');
    } else {
      const text = state === 'loading' ? 'Cargando puntos...' : 'Mapa temporalmente no disponible';
      this.componentMessageEl.textContent = text;
      this.componentMessageEl.removeAttribute('hidden');
      this.mapLayoutEl.setAttribute('hidden', '');
    }
  }

  private centerMapOnPoint(lat: number, lng: number, targetZoom = 14) {
    const g = (window as any).google;
    if (!g || !this.map || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }
    const currentZoom = typeof this.map.getZoom === 'function' ? this.map.getZoom() : null;
    const needsZoom = !currentZoom || currentZoom < targetZoom;
    this.animateMapPan(lat, lng, () => {
      if (needsZoom) {
        this.map.setZoom(targetZoom);
      }
    });
  }

  private async focusPointWithZoom(lat: number, lng: number) {
    const g = (window as any).google;
    if (!g || !this.map || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    await this.animateZoomTo(8, 800);
    await this.wait(500);
    await new Promise<void>((resolve) => {
      this.animateMapPan(lat, lng, resolve);
    });
    await this.wait(500);
    await this.animateZoomTo(18, 800);
  }

  private wait(ms: number) {
    return new Promise<void>((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  private async animateZoomTo(targetZoom: number, duration = 600) {
    const g = (window as any).google;
    if (
      !g ||
      !this.map ||
      typeof this.map.getZoom !== 'function' ||
      typeof this.map.setZoom !== 'function'
    ) {
      return;
    }
    const startZoom = this.map.getZoom();
    if (!Number.isFinite(startZoom)) {
      this.map.setZoom(targetZoom);
      return;
    }
    const delta = targetZoom - startZoom;
    if (Math.abs(delta) < 0.01) {
      this.map.setZoom(targetZoom);
      return;
    }

    await new Promise<void>((resolve) => {
      const startTime = performance.now();
      const ease = (t: number) => 1 - Math.pow(1 - t, 3);

      const step = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = ease(progress);
        const nextZoom = startZoom + delta * eased;
        this.map.setZoom(Math.round(nextZoom * 100) / 100);
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          this.map.setZoom(targetZoom);
          resolve();
        }
      };

      requestAnimationFrame(step);
    });
  }

  private animateMapPan(targetLat: number, targetLng: number, onComplete?: () => void) {
    const g = (window as any).google;
    if (!g || !this.map) {
      return;
    }
    if (this.panAnimationFrame !== null) {
      cancelAnimationFrame(this.panAnimationFrame);
      this.panAnimationFrame = null;
    }
    const startCenter = this.map.getCenter();
    if (!startCenter) {
      this.map.setCenter({ lat: targetLat, lng: targetLng });
      onComplete?.();
      return;
    }
    const startLat = startCenter.lat();
    const startLng = startCenter.lng();
    const deltaLat = targetLat - startLat;
    const deltaLng = targetLng - startLng;
    const duration = 600;
    const startTime = performance.now();

    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = ease(progress);
      const nextLat = startLat + deltaLat * eased;
      const nextLng = startLng + deltaLng * eased;
      this.map.setCenter({ lat: nextLat, lng: nextLng });
      if (progress < 1) {
        this.panAnimationFrame = requestAnimationFrame(step);
      } else {
        this.panAnimationFrame = null;
        onComplete?.();
      }
    };

    this.panAnimationFrame = requestAnimationFrame(step);
  }

  private static ensureFontAwesome() {
    if (RsMap.fontAwesomeAttached) {
      return;
    }
    const existing = document.querySelector(`link[href="${FONT_AWESOME_LOCAL_PATH}"]`);
    if (existing) {
      RsMap.fontAwesomeAttached = true;
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_AWESOME_LOCAL_PATH;
    link.crossOrigin = 'anonymous';
    link.referrerPolicy = 'no-referrer';
    link.addEventListener('load', () => {
      RsMap.fontAwesomeAttached = true;
    });
    document.head.appendChild(link);
  }

  private createIconElement(className: string) {
    const icon = document.createElement('i');
    icon.className = className;
    icon.setAttribute('aria-hidden', 'true');
    return icon;
  }

  private getWhatsappLink(phone: string) {
    const digits = phone.replace(/\D+/g, '');
    if (!digits) {
      return null;
    }
    return `https://wa.me/${digits}`;
  }
}
